import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, Observable, of, throwError } from 'rxjs';
import { AppConfig } from 'src/app/app.config';
import { InventoryMessageRecipient, InventoryStorage, Permission } from './models/inventory.model';

@Injectable()
export class InventoryService {

    private urlIwmBackend: string = this.config.backendIwmUri;

    constructor(
        private config: AppConfig,
        private http: HttpClient,
        private oauthService: OAuthService) { }

    get header(): HttpHeaders {
        return new HttpHeaders({ Authorization: `Bearer ${this.oauthService.getAccessToken()}` });
    }

    public getStorages() {
        return this.http
            .get<InventoryStorage>(`${this.urlIwmBackend}InventoryApi/Inventories?showArchived=true`);
    }

    public getStorage(storageId: string): Observable<InventoryStorage> {
        return this.http
            .get<InventoryStorage>(`${this.urlIwmBackend}InventoryApi/Inventories/${storageId}`);
    }

    public createStorage(storage: InventoryStorage): Observable<any> {
        const newStorage: any = {
            Name: storage.Name,
            Description: storage.Description,
            StreetAddress: storage.StreetAddress,
            PostCode: storage.PostCode,
            PostOffice: storage.PostOffice,
            County: storage.County,
            Latitude: storage.Latitude,
            Longitude: storage.Longitude
        }
        return this.http
            .post(`${this.urlIwmBackend}InventoryApi/Inventories`, newStorage);
    }

    public deleteInventoryFolder(id: string) {
        return this.http
            .delete(`${this.config.backendIwmUri}InventoryApi/InventoryFolders/${id}`);
    }

    public patchFolderName(folderId: string, name: string): Observable<any> {
        return this.http
            .patch(`${this.config.backendIwmUri}/InventoryApi/InventoryFolders(${folderId})`, { Name: name });
    }

    public getInventoryFolders(id: string) {
        return this.http
            .get(`${this.config.backendIwmUri}InventoryApi/InventoryFolders?%24filter=InventoryId%20%20eq%20${id}&%24orderby=Name`);
    }

    public createFolder(folder: { Name: string, InventoryId: string }): Observable<any> {
        return this.http
            .post(`${this.config.backendIwmUri}InventoryApi/InventoryFolders`, folder);
    }

    public getInventoryItem(Id: string): Observable<any> {
        return this.http
            .get(`${this.config.backendIwmUri}InventoryApi/InventoryItems/${Id}`);
    }

    public getInventoryItems(folderId: string, includeArchivedItems?: boolean): Observable<any> {
        const archivedItems = includeArchivedItems ? '' : 'AND ArchivedTime eq null';
        return this.http
            .get(`${this.config.backendIwmUri}InventoryApi/InventoryItems?$filter=InventoryFolderId eq ${folderId} ${archivedItems} &%24orderby=Name`);
    }

    public addInventoryItem(item: any): Observable<any> {
        return this.http
            .post(`${this.config.backendIwmUri}InventoryApi/InventoryItems`, item);
    }

    public updateInventoryItem(item: any, Id: string): Observable<any> {
        return this.http
            .patch(`${this.config.backendIwmUri}InventoryApi/InventoryItems/${Id}`, item);
    }

    public deleteInventoryItem(Id: string): Observable<any> {
        return this.http
            .delete(`${this.config.backendIwmUri}InventoryApi/InventoryItems/${Id}`);
    }

    public getItemEntries(itemId: string): Observable<any> {
        return this.http
            .get(`${this.config.backendIwmUri}/InventoryApi/InventoryBalanceEntries?%24filter=InventoryItemId%20eq%20${itemId}&%24orderby=Modified desc`);
    }

    public addItemEntry(entry: any): Observable<any> {
        return this.http
            .post(`${this.config.backendIwmUri}InventoryApi/InventoryBalanceEntries`, entry);
    }

    public uppdateItemEntry(entry: any, Id: string): Observable<any> {
        return this.http
            .patch(`${this.config.backendIwmUri}InventoryApi/InventoryBalanceEntries/${Id}`, entry);
    }

    public updateStorage(storage: InventoryStorage): Observable<any> {
        const patchStorage = {
            Name: storage.Name,
            Description: storage.Description,
            Archived: storage.Archived ? new Date() : null,
            StreetAddress: storage.StreetAddress,
            PostCode: storage.PostCode,
            PostOffice: storage.PostOffice,
            County: storage.County
        }

        return this.http
            .patch(`${this.urlIwmBackend}InventoryApi/Inventories/${storage.Id}`, patchStorage);
    }

    public getPermissions(storageId: string): Observable<any> {
        return this.http
            .get(`${this.config.backendIwmUri}InventoryApi/Inventories/${storageId}/GetPermissions`);
    }

    public setPermission(storageId: string, permission: Permission): Observable<any> {
        return this.http
            .post(`${this.config.backendIwmUri}InventoryApi/Inventories/${storageId}/SetPermission`, permission);
    }

    public getInventoryRecipients(storageId: string) {
        return this.http
            .get<InventoryMessageRecipient>(`${this.urlIwmBackend}InventoryApi/InventoryMessageRecipients?%24filter=InventoryId%20eq%20${storageId}`);
    }

    public addInventoryRecipient(inventoryRecipient: InventoryMessageRecipient): Observable<any> {
        return this.http
            .post(`${this.config.backendIwmUri}InventoryApi/InventoryMessageRecipients`, inventoryRecipient);
    }

    public removeInventoryRecipient(inventoryRecipient: InventoryMessageRecipient): Observable<any> {
        return this.http
            .delete(`${this.config.backendIwmUri}InventoryApi/InventoryMessageRecipients/${inventoryRecipient.Id}`);
    }

    public searchItems(inventoryId: string, searchText: string, includeArchivedItems: boolean): Observable<any> {
        const archivedItems = includeArchivedItems ? '' : 'AND ArchivedTime eq null';
        return this.http
            .get(`${this.config.backendIwmUri}InventoryApi/InventoryItems?$filter=InventoryId eq ${inventoryId} ${archivedItems} AND (contains(Identifier%2C%20'${searchText}') OR
            contains(Name%2C%20'${searchText}') OR contains(SerialNumber%2C%20'${searchText}'))`);
    }

    public getAttachments(itemId: string) {
        return this.http
            .get(`${this.config.backendIwmUri}InventoryApi/InventoryAttachments?$filter=InventoryItemId eq ${itemId}&%24orderby=Created%20desc`);
    }

    public getAttachmentTempUrl(id: string): Observable<any> {
        const url = `${this.config.backendIwmUri}InventoryApi/api/InventoryFile/GetTemporaryUrl?inventoryAttachmentId=${id}`;
        return this.http.get(url);
    }

    public deleteAttachment(attachmentId: string) {
        return this.http
            .delete(`${this.config.backendIwmUri}InventoryApi/InventoryAttachments/${attachmentId}`);
    }

    public archiveItems(ids: string[]) {
        return this.http
            .post(`${this.config.backendIwmUri}InventoryApi/InventoryItems/BatchArchive`, { InventoryItems: ids });
    }

    public UnarchiveItems(ids: string[]) {
        return this.http
            .post(`${this.config.backendIwmUri}InventoryApi/InventoryItems/BatchRestore`, { InventoryItems: ids });
    }

    public getThumbnails(itemId: string) {
        let url = `${this.config.backendIwmUri}InventoryApi/api/InventoryFile/DownloadThumbnailsAsZip/${itemId}`;
        return this.http.get(url, {
            responseType: 'blob',
            withCredentials: true,
            headers: this.header
        });
    }

    public getCompressedPictures(itemId: string) {
        let url = `${this.config.backendIwmUri}InventoryApi/api/InventoryFile/DownloadCompressedAsZip/${itemId}`;
        return this.http.get(url, {
            responseType: 'blob',
            withCredentials: true,
            headers: this.header
        });
    }

    public transferItemsToStorage(itemsToTransfer: any): Promise<any> {
        let promise = new Promise<void>((resolve, reject) => {
            this.http
                .post(`${this.config.backendIwmUri}InventoryApi/InventoryItems/MoveToInventory`, itemsToTransfer).toPromise()
                .then(
                    res => {
                        resolve();
                    },
                    msg => {
                        reject(msg);
                    }
                );
        });
        return promise;
    }

    public exportInventoryToExcel(inventoryId: string, includeArchivedItems: boolean): Observable<Blob> {
        return this.http.get(`${this.config.backendIwmUri}InventoryApi/api/InventoryFile/Export?inventoryId=${inventoryId}&includeArchivedItems=${includeArchivedItems}`, { responseType: 'blob' });
    }
}
